import type {
  RealmClaimed,
  RealmFound,
  RealmOpened,
  RealmPlayed,
  RealmRefusal,
  RealmSlot,
  RealmView,
  RealmWished,
} from '@goodtime/shared';
import { RealmRefused } from '@goodtime/engine';
import { api, ApiError } from './client';
import { withRetry } from './road';

/**
 * The realm map's places, the warm pool, the fountain, the daily games and
 * the secrets (flag realm_map). Every write sends an idempotency key, the
 * same on each retry of one tap, so a retry after a lost answer gets the
 * server's first answer back and pays once. A request the server turns
 * down (402, 409 with a reason) rejects with the engine's
 * {@link RealmRefused}.
 */

/** The player's realm, or null when the map isn't on for them (204). */
export async function fetchRealm(playerId: string): Promise<RealmView | null> {
  const view = await api<RealmView | undefined>(`/players/${encodeURIComponent(playerId)}/realm`);
  return view ?? null;
}

/** The slots the player has played, for the world maps' plates (flag
 *  world_maps): each one's stars, and a road slot's chapter. None when the
 *  map is off for them or the server is older. */
export async function fetchRealmSlots(playerId: string): Promise<RealmSlot[]> {
  const view = await fetchRealm(playerId);
  return Array.isArray(view?.slots) ? view.slots : [];
}

export function openPlace(playerId: string, placeId: string): Promise<RealmOpened> {
  return write(`/players/${encodeURIComponent(playerId)}/realm/places/${encodeURIComponent(placeId)}/open`, `open-${placeId}`);
}

export function playPlace(playerId: string, placeId: string): Promise<RealmPlayed> {
  return write(`/players/${encodeURIComponent(playerId)}/realm/places/${encodeURIComponent(placeId)}/play`, `play-${placeId}`);
}

export function claimSprings(playerId: string): Promise<RealmClaimed> {
  return write(`/players/${encodeURIComponent(playerId)}/realm/springs/claim`, 'springs');
}

export function makeWish(playerId: string): Promise<RealmWished> {
  return write(`/players/${encodeURIComponent(playerId)}/realm/fountain/wish`, 'wish');
}

export function findSecret(playerId: string, secretId: string): Promise<RealmFound> {
  return write(`/players/${encodeURIComponent(playerId)}/realm/secrets/${encodeURIComponent(secretId)}`, `secret-${secretId}`);
}

/** Why the server turned a realm request down, or null for any other error. */
export function realmRefusal(err: unknown): RealmRefusal | null {
  if (!(err instanceof ApiError) || (err.status !== 402 && err.status !== 409)) return null;
  try {
    const body = JSON.parse(err.body) as Partial<RealmRefusal>;
    return typeof body.error === 'string' ? (body as RealmRefusal) : null;
  } catch {
    return null;
  }
}

async function write<T>(path: string, what: string): Promise<T> {
  const key = `realm-${what}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`.slice(0, 64);
  try {
    return await withRetry(() => api<T>(path, { method: 'POST', headers: { 'Idempotency-Key': key } }));
  } catch (err) {
    const refusal = realmRefusal(err);
    if (refusal) throw new RealmRefused(refusal);
    throw err;
  }
}
